/**
 * ChainVault WP - Exchange Page
 *
 * Real in-app token swaps powered by:
 * - Jupiter Aggregator API (Solana)
 * - Uniswap V3 Router (EVM chains)
 *
 * @package ChainVault
 * @since   1.0.0
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useWallet } from '../context/WalletContext';
import toast from 'react-hot-toast';

/* ── Token definitions ─────────────────────────────────────── */
const CG = 'https://assets.coingecko.com/coins/images';
const TOKEN_ICONS = {
    ETH:   CG + '/279/small/ethereum.png',
    WBTC:  CG + '/7598/small/wrapped_bitcoin_wbtc.png',
    USDC:  CG + '/6319/small/USD_Coin_icon.png',
    USDT:  CG + '/325/small/Tether.png',
    UNI:   CG + '/12504/small/uniswap-uni.png',
    LINK:  CG + '/877/small/chainlink-new-logo.png',
    BNB:   CG + '/825/small/bnb-icon2_2x.png',
    MATIC: CG + '/4713/small/polygon.png',
    DAI:   CG + '/9956/small/Badge_Dai.png',
    SOL:   CG + '/4128/small/solana.png',
    USDC_SOL: CG + '/6319/small/USD_Coin_icon.png',
    JUP:   CG + '/34188/small/jup.png',
    BONK:  CG + '/28600/small/bonk.jpg',
};

const EVM_TOKENS = {
    ethereum: [
        { symbol: 'ETH',  name: 'Ethereum',       icon: TOKEN_ICONS.ETH,  address: 'native',                                       decimals: 18 },
        { symbol: 'USDC', name: 'USD Coin',        icon: TOKEN_ICONS.USDC, address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', decimals: 6  },
        { symbol: 'USDT', name: 'Tether',          icon: TOKEN_ICONS.USDT, address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', decimals: 6  },
        { symbol: 'WBTC', name: 'Wrapped Bitcoin', icon: TOKEN_ICONS.WBTC, address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', decimals: 8  },
        { symbol: 'UNI',  name: 'Uniswap',         icon: TOKEN_ICONS.UNI,  address: '0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984', decimals: 18 },
        { symbol: 'LINK', name: 'Chainlink',        icon: TOKEN_ICONS.LINK, address: '0x514910771AF9Ca656af840dff83E8264EcF986CA', decimals: 18 },
        { symbol: 'DAI',  name: 'Dai',              icon: TOKEN_ICONS.DAI,  address: '0x6B175474E89094C44Da98b954EedeAC495271d0F', decimals: 18 },
    ],
    bsc: [
        { symbol: 'BNB',  name: 'BNB',     icon: TOKEN_ICONS.BNB,  address: 'native',                                       decimals: 18 },
        { symbol: 'USDT', name: 'Tether',   icon: TOKEN_ICONS.USDT, address: '0x55d398326f99059fF775485246999027B3197955', decimals: 18 },
        { symbol: 'USDC', name: 'USD Coin', icon: TOKEN_ICONS.USDC, address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', decimals: 18 },
    ],
    polygon: [
        { symbol: 'MATIC', name: 'Polygon',     icon: TOKEN_ICONS.MATIC, address: 'native',                                       decimals: 18 },
        { symbol: 'USDC',  name: 'USD Coin',    icon: TOKEN_ICONS.USDC,  address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174', decimals: 6  },
        { symbol: 'USDT',  name: 'Tether',      icon: TOKEN_ICONS.USDT,  address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', decimals: 6  },
    ],
    base: [
        { symbol: 'ETH',  name: 'Ethereum', icon: TOKEN_ICONS.ETH,  address: 'native',                                       decimals: 18 },
        { symbol: 'USDC', name: 'USD Coin', icon: TOKEN_ICONS.USDC, address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', decimals: 6  },
        { symbol: 'DAI',  name: 'Dai',      icon: TOKEN_ICONS.DAI,  address: '0x50c5725949A6F0c72E6C4a641F24049A917DB0Cb', decimals: 18 },
    ],
};

const SOL_TOKENS = [
    { symbol: 'SOL',  name: 'Solana',   icon: TOKEN_ICONS.SOL,      mint: 'native', decimals: 9 },
    { symbol: 'USDC', name: 'USD Coin', icon: TOKEN_ICONS.USDC_SOL, mint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', decimals: 6 },
    { symbol: 'USDT', name: 'Tether',   icon: TOKEN_ICONS.USDT,     mint: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB', decimals: 6 },
    { symbol: 'JUP',  name: 'Jupiter',  icon: TOKEN_ICONS.JUP,      mint: 'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN', decimals: 6 },
    { symbol: 'BONK', name: 'Bonk',     icon: TOKEN_ICONS.BONK,     mint: 'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263', decimals: 5 },
];

/* CoinGecko IDs for Solana-side tokens, used to compute USD values
   since Jupiter's quote response only returns token amounts, not fiat values. */
const SOL_COIN_IDS = {
    SOL:  'solana',
    USDC: 'usd-coin',
    USDT: 'tether',
    JUP:  'jupiter-exchange-solana',
    BONK: 'bonk',
};

/* Jupiter now requires api.jup.ag with an API key (quote-api.jup.ag/v6 was
   permanently deprecated Oct 1 2025). Falls back to unauthenticated request
   if no key is configured (rate-limited, may fail under load). */
function getJupiterHeaders() {
    const key = ( window.chainvaultSettings && window.chainvaultSettings.jupiterApiKey ) || '';
    return key ? { 'x-api-key': key } : {};
}

/* Uniswap V3 Router address (same on all EVM chains) */
const UNISWAP_V3_ROUTER = '0xE592427A0AEce92De3Edee1F18E0157C05861564';
const UNISWAP_FEE_TIERS  = [500, 3000, 10000]; /* 0.05%, 0.3%, 1% */

/* ── Token Icon component ──────────────────────────────────── */
function TokenIcon({ icon, symbol, size = 24 }) {
    const [err, setErr] = useState(false);
    if (!icon || err) return React.createElement('div', {
        style: { width:size, height:size, borderRadius:'50%', background:'linear-gradient(135deg,#7C63FF,#FC72FF)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:size*0.4, color:'#fff', fontWeight:'700', flexShrink:0 }
    }, symbol?.[0]);
    return React.createElement('img', { src:icon, style:{width:size,height:size,borderRadius:'50%',objectFit:'cover',flexShrink:0}, onError:()=>setErr(true) });
}

/* ── Token Picker ──────────────────────────────────────────── */
function TokenPicker({ tokens, selected, onSelect, label }) {
    const [open, setOpen] = useState(false);
    const tok = tokens.find(t=>t.symbol===selected) || tokens[0];
    return React.createElement('div', { style:{position:'relative'} },
        React.createElement('button', {
            onClick: ()=>setOpen(!open),
            style: { display:'flex', alignItems:'center', gap:8, background:'rgba(255,255,255,0.08)', border:'none', borderRadius:24, padding:'8px 14px 8px 8px', cursor:'pointer', color:'#fff', fontWeight:'700', fontSize:15 }
        },
            React.createElement(TokenIcon, {icon:tok.icon, symbol:tok.symbol, size:28}),
            tok.symbol,
            React.createElement('span', {style:{color:'#888',fontSize:12}}, '▼')
        ),
        open && React.createElement('div', { style:{position:'absolute',right:0,top:48,background:'#1a1a2e',border:'1px solid rgba(255,255,255,0.1)',borderRadius:16,zIndex:200,minWidth:200,maxHeight:300,overflowY:'auto',boxShadow:'0 16px 40px rgba(0,0,0,0.6)'} },
            tokens.map(t => React.createElement('div', {
                key:t.symbol,
                onClick:()=>{onSelect(t.symbol);setOpen(false);},
                style:{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',cursor:'pointer',background:selected===t.symbol?'rgba(124,99,255,0.2)':' transparent',transition:'background 0.15s'}
            },
                React.createElement(TokenIcon,{icon:t.icon,symbol:t.symbol,size:32}),
                React.createElement('div',null,
                    React.createElement('div',{style:{color:'#fff',fontWeight:'700',fontSize:14}},t.symbol),
                    React.createElement('div',{style:{color:'#888',fontSize:12}},t.name)
                )
            ))
        )
    );
}

/* ── Main Exchange Page ────────────────────────────────────── */
export default function ExchangePage() {
    const { address, walletType, selectedNetwork, builtInWallet } = useWallet();
    const isSolana = selectedNetwork === 'solana';
    const tokens   = isSolana ? SOL_TOKENS : (EVM_TOKENS[selectedNetwork] || EVM_TOKENS.ethereum);

    const [tab,        setTab       ] = useState('swap');
    const [sellToken,  setSellToken ] = useState(tokens[0].symbol);
    const [buyToken,   setBuyToken  ] = useState(tokens[1].symbol);
    const [sellAmount, setSellAmount] = useState('');
    const [buyAmount,  setBuyAmount ] = useState('');
    const [sellBal,    setSellBal   ] = useState('—');
    const [usdValue,   setUsdValue  ] = useState('');
    const [buyUsdValue, setBuyUsdValue] = useState('');
    const [isSwapping, setIsSwapping] = useState(false);
    const [slippage,   setSlippage  ] = useState(0.5);
    const [showSettings, setShowSettings] = useState(false);
    const [jupQuote,   setJupQuote  ] = useState(null);

    const walletAddress = walletType === 'builtin' && builtInWallet
        ? (isSolana ? builtInWallet.solana?.address : builtInWallet.ethereum?.address)
        : address;

    /* Reset tokens when network changes */
    useEffect(()=>{
        const newTokens = isSolana ? SOL_TOKENS : (EVM_TOKENS[selectedNetwork] || EVM_TOKENS.ethereum);
        setSellToken(newTokens[0].symbol);
        setBuyToken(newTokens[1].symbol);
        setSellAmount(''); setBuyAmount(''); setJupQuote(null);
    },[selectedNetwork]); // eslint-disable-line

    /* Fetch sell token balance — branches on the SPECIFIC selected token,
       not just the native coin, so switching to USDT/USDC/etc shows that
       token's actual balance instead of always showing the native balance. */
    useEffect(()=>{
        if(!walletAddress){setSellBal('—');return;}
        const fetchBal = async()=>{
            try{
                if(isSolana){
                    const solTok = SOL_TOKENS.find(t=>t.symbol===sellToken);
                    if(!solTok||solTok.mint==='native'){
                        const web3 = await import('@solana/web3.js');
                        const conn = new web3.Connection('https://api.mainnet-beta.solana.com');
                        const bal  = await conn.getBalance(new web3.PublicKey(walletAddress));
                        setSellBal((bal/web3.LAMPORTS_PER_SOL).toFixed(4));
                    } else {
                        const web3 = await import('@solana/web3.js');
                        const conn = new web3.Connection('https://api.mainnet-beta.solana.com');
                        const owner = new web3.PublicKey(walletAddress);
                        const mint  = new web3.PublicKey(solTok.mint);
                        const resp  = await conn.getParsedTokenAccountsByOwner(owner,{mint});
                        const total = resp.value.reduce((sum,acc)=>sum+(acc.account.data.parsed.info.tokenAmount.uiAmount||0),0);
                        setSellBal(total.toFixed(4));
                    }
                } else {
                    const evmTok = (EVM_TOKENS[selectedNetwork]||[]).find(t=>t.symbol===sellToken);
                    const {ethers} = await import('ethers');
                    const rpc = selectedNetwork==='bsc'?'https://bsc-dataseed.binance.org':selectedNetwork==='polygon'?'https://polygon-bor-rpc.publicnode.com':selectedNetwork==='base'?'https://mainnet.base.org':'https://ethereum-rpc.publicnode.com';
                    const provider = new ethers.JsonRpcProvider(rpc);
                    if(!evmTok||evmTok.address==='native'){
                        const bal = await provider.getBalance(walletAddress);
                        setSellBal(parseFloat(ethers.formatEther(bal)).toFixed(4));
                    } else {
                        const abi = ['function balanceOf(address) view returns (uint256)'];
                        const contract = new ethers.Contract(evmTok.address,abi,provider);
                        const bal = await contract.balanceOf(walletAddress);
                        setSellBal(parseFloat(ethers.formatUnits(bal,evmTok.decimals)).toFixed(4));
                    }
                }
            }catch(e){setSellBal('—');}
        };
        fetchBal();
    },[walletAddress,sellToken,isSolana,selectedNetwork]); // eslint-disable-line

    /* Get quote when amount changes */
    const getQuote = useCallback(async(amount)=>{
        setSellAmount(amount);
        if(!amount||parseFloat(amount)<=0){setBuyAmount('');setUsdValue('');setBuyUsdValue('');setJupQuote(null);return;}
        try{
            if(isSolana){
                const sellTok = SOL_TOKENS.find(t=>t.symbol===sellToken);
                const buyTok  = SOL_TOKENS.find(t=>t.symbol===buyToken);
                const inputMint  = sellTok?.mint==='native'?'So11111111111111111111111111111111111111112':sellTok?.mint;
                const outputMint = buyTok?.mint==='native'?'So11111111111111111111111111111111111111112':buyTok?.mint;
                const sellDecimals = sellTok?.decimals ?? 9;
                const buyDecimals  = buyTok?.decimals ?? 9;
                const lamports   = Math.floor(parseFloat(amount)*Math.pow(10,sellDecimals));
                const res  = await fetch(`https://api.jup.ag/swap/v1/quote?inputMint=${inputMint}&outputMint=${outputMint}&amount=${lamports}&slippageBps=${Math.floor(slippage*100)}`,{headers:getJupiterHeaders()});
                const data = await res.json();
                if(data.outAmount){
                    setJupQuote(data);
                    const outAmt = (parseInt(data.outAmount)/Math.pow(10,buyDecimals)).toFixed(6);
                    setBuyAmount(outAmt);

                    /* Compute USD values via CoinGecko since Jupiter's quote has no fiat data */
                    const sellId = SOL_COIN_IDS[sellToken];
                    const buyId  = SOL_COIN_IDS[buyToken];
                    if(sellId&&buyId){
                        try{
                            const priceRes  = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${sellId},${buyId}&vs_currencies=usd`);
                            const priceData = await priceRes.json();
                            if(priceData[sellId]) setUsdValue('$'+(parseFloat(amount)*priceData[sellId].usd).toFixed(2));
                            if(priceData[buyId])  setBuyUsdValue('$'+(parseFloat(outAmt)*priceData[buyId].usd).toFixed(2));
                        }catch(priceErr){console.error('Solana price fetch error',priceErr);}
                    }
                } else {
                    setBuyAmount('');
                    toast.error(data.error || 'No route found for this pair');
                }
            } else {
                /* Use CoinGecko for price estimation on EVM */
                const COIN_IDS = {ETH:'ethereum',USDC:'usd-coin',USDT:'tether',WBTC:'wrapped-bitcoin',UNI:'uniswap',LINK:'chainlink',DAI:'dai',BNB:'binancecoin',MATIC:'matic-network'};
                const fromId = COIN_IDS[sellToken];
                const toId   = COIN_IDS[buyToken];
                if(fromId&&toId){
                    const res  = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${fromId},${toId}&vs_currencies=usd`);
                    const data = await res.json();
                    if(data[fromId]&&data[toId]){
                        const fromUsd = parseFloat(amount)*data[fromId].usd;
                        const toAmt   = (fromUsd/data[toId].usd*0.997).toFixed(6);
                        setBuyAmount(toAmt);
                        setUsdValue('$'+fromUsd.toFixed(2));
                        setBuyUsdValue('$'+(parseFloat(toAmt)*data[toId].usd).toFixed(2));
                    }
                }
            }
        }catch(e){console.error('Quote error',e);}
    },[sellToken,buyToken,isSolana,slippage]);

    /* Execute swap */
    const handleSwap = async()=>{
        if(!sellAmount||!walletAddress){toast.error('Enter amount and connect wallet');return;}
        setIsSwapping(true);
        try{
            if(isSolana){
                if(!jupQuote){toast.error('No quote available');return;}
                const swapRes  = await fetch('https://api.jup.ag/swap/v1/swap',{
                    method:'POST', headers:{'Content-Type':'application/json',...getJupiterHeaders()},
                    body:JSON.stringify({quoteResponse:jupQuote,userPublicKey:walletAddress,wrapAndUnwrapSol:true})
                });
                const swapData = await swapRes.json();
                if(!swapData.swapTransaction){throw new Error('No swap transaction returned');}

                /* Sign with built-in wallet and submit via PHP relay */
                const web3 = await import('@solana/web3.js');
                const payer = web3.Keypair.fromSecretKey(new Uint8Array(builtInWallet.solana.secretKey));
                const txBuf = Buffer.from(swapData.swapTransaction,'base64');
                const tx    = web3.VersionedTransaction.deserialize(txBuf);
                tx.sign([payer]);
                const signed = Buffer.from(tx.serialize()).toString('base64');

                const wpBase = window.chainvaultSettings?.siteUrl||'/wp-json/chainvault/v1';
                const relayRes  = await fetch(wpBase+'/solana/relay',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({transaction:signed,network:'mainnet'})});
                const relayData = await relayRes.json();
                if(relayData.error)throw new Error(JSON.stringify(relayData.error));

                toast.success('Swap successful! '+relayData.signature?.slice(0,16)+'...');
                setSellAmount(''); setBuyAmount(''); setJupQuote(null);

            } else {
                /* EVM swap via Uniswap V3 Router */
                const {ethers} = await import('ethers');
                const networkTokens = EVM_TOKENS[selectedNetwork]||EVM_TOKENS.ethereum;
                const sellTok = networkTokens.find(t=>t.symbol===sellToken);
                const buyTok  = networkTokens.find(t=>t.symbol===buyToken);
                const rpcMap = {ethereum:'https://ethereum-rpc.publicnode.com',bsc:'https://bsc-dataseed.binance.org',polygon:'https://polygon-bor-rpc.publicnode.com',base:'https://mainnet.base.org'};
                const rpc = rpcMap[selectedNetwork]||rpcMap.ethereum;
                let signer;
                if(walletType==='builtin'&&builtInWallet?.ethereum?.privateKey){
                    const provider = new ethers.JsonRpcProvider(rpc);
                    signer = new ethers.Wallet(builtInWallet.ethereum.privateKey, provider);
                } else if(walletType==='metamask'&&window.ethereum){
                    const provider = new ethers.BrowserProvider(window.ethereum);
                    signer = await provider.getSigner();
                } else { throw new Error('No EVM wallet connected'); }
                const amountIn = ethers.parseUnits(sellAmount, sellTok?.decimals||18);
                const ROUTER_ABI = ['function exactInputSingle((address tokenIn,address tokenOut,uint24 fee,address recipient,uint256 deadline,uint256 amountIn,uint256 amountOutMinimum,uint160 sqrtPriceLimitX96)) external payable returns (uint256 amountOut)'];
                const router = new ethers.Contract(UNISWAP_V3_ROUTER, ROUTER_ABI, signer);
                const WETH = '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2';
                const tokenIn  = sellTok?.address==='native'?WETH:sellTok?.address;
                const tokenOut = buyTok?.address==='native'?WETH:buyTok?.address;
                const deadline = Math.floor(Date.now()/1000)+1200;
                if(sellTok?.address!=='native'){
                    const erc20 = new ethers.Contract(sellTok.address,['function allowance(address,address) view returns(uint256)','function approve(address,uint256) returns(bool)'],signer);
                    const allowance = await erc20.allowance(await signer.getAddress(), UNISWAP_V3_ROUTER);
                    if(allowance < amountIn){ toast.loading('Approving...',{id:'swap'}); const a = await erc20.approve(UNISWAP_V3_ROUTER,ethers.MaxUint256); await a.wait(); }
                }
                toast.loading('Swapping...',{id:'swap'});
                const params = {tokenIn,tokenOut,fee:3000,recipient:await signer.getAddress(),deadline,amountIn,amountOutMinimum:0n,sqrtPriceLimitX96:0n};
                const tx = sellTok?.address==='native' ? await router.exactInputSingle(params,{value:amountIn}) : await router.exactInputSingle(params);
                await tx.wait();
                toast.success('Swap successful! '+tx.hash.slice(0,16)+'...',{id:'swap'});
                setSellAmount(''); setBuyAmount('');
                /* Refresh balances after swap */
                setTimeout(()=>window.dispatchEvent(new Event('chainvault_tokens_updated')),2000);
            }
        }catch(e){
            toast.error('Swap failed: '+e.message?.slice(0,60));
        }finally{setIsSwapping(false);}
    };

    const swapDirection = ()=>{
        const tmp = sellToken; setSellToken(buyToken); setBuyToken(tmp);
        setSellAmount(''); setBuyAmount(''); setJupQuote(null);
    };

    const sellTok = tokens.find(t=>t.symbol===sellToken)||tokens[0];
    const buyTok  = tokens.find(t=>t.symbol===buyToken)||tokens[1];
    const hasAmount = parseFloat(sellAmount)>0;

    return (
        <div style={{minHeight:'100vh',background:'#0a0a14',display:'flex',flexDirection:'column',alignItems:'center',padding:'24px 16px'}}>

            {/* Settings Modal */}
            { showSettings && (
                <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setShowSettings(false)}>
                    <div style={{background:'#1a1a2e',borderRadius:20,padding:24,minWidth:300,border:'1px solid rgba(255,255,255,0.1)'}} onClick={e=>e.stopPropagation()}>
                        <h3 style={{color:'#fff',marginBottom:16}}>Swap Settings</h3>
                        <label style={{color:'#aaa',fontSize:13,display:'block',marginBottom:8}}>Slippage tolerance (%)</label>
                        <div style={{display:'flex',gap:8,marginBottom:12}}>
                            {[0.1,0.5,1.0].map(v=>
                                <button key={v} onClick={()=>setSlippage(v)} style={{flex:1,padding:'8px',border:'none',borderRadius:8,cursor:'pointer',background:slippage===v?'#7C63FF':'rgba(255,255,255,0.08)',color:'#fff',fontWeight:'700'}}>
                                    {v}%
                                </button>
                            )}
                        </div>
                        <input type="number" value={slippage} onChange={e=>setSlippage(parseFloat(e.target.value)||0.5)}
                            style={{width:'100%',padding:'10px',background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#fff',fontSize:14,boxSizing:'border-box'}} />
                        <button onClick={()=>setShowSettings(false)} style={{marginTop:16,width:'100%',padding:12,background:'#7C63FF',border:'none',borderRadius:12,color:'#fff',fontWeight:'700',cursor:'pointer'}}>Done</button>
                    </div>
                </div>
            )}

            <div style={{width:'100%',maxWidth:480}}>

                {/* Tab bar */}
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
                    <div style={{display:'flex',gap:4,background:'rgba(255,255,255,0.05)',borderRadius:24,padding:4}}>
                        <button style={{padding:'8px 24px',border:'none',borderRadius:20,cursor:'pointer',fontWeight:'700',fontSize:14,background:'#fff',color:'#000'}}>
                            Swap
                        </button>
                    </div>
                    <button onClick={()=>setShowSettings(true)} style={{background:'rgba(255,255,255,0.08)',border:'none',borderRadius:'50%',width:40,height:40,cursor:'pointer',fontSize:18,color:'#aaa',display:'flex',alignItems:'center',justifyContent:'center'}}>⚙</button>
                </div>

                { tab==='swap' && (
                    <>
                        {/* Sell card */}
                        <div style={{background:'#1a1a1a',borderRadius:20,padding:'16px 20px',marginBottom:4,border:'1px solid rgba(255,255,255,0.06)'}}>
                            <div style={{color:'#888',fontSize:13,marginBottom:12}}>Sell</div>
                            <div style={{display:'flex',alignItems:'center',gap:12}}>
                                <input
                                    type="number" placeholder="0" value={sellAmount}
                                    onChange={e=>getQuote(e.target.value)}
                                    style={{flex:1,background:'transparent',border:'none',outline:'none',color:'#fff',fontSize:36,fontWeight:'300',fontFamily:'inherit'}}
                                />
                                <TokenPicker tokens={tokens} selected={sellToken} onSelect={setSellToken} />
                            </div>
                            <div style={{display:'flex',justifyContent:'space-between',marginTop:8,fontSize:13,color:'#888'}}>
                                <span>{usdValue||'$0'}</span>
                                <span style={{cursor:'pointer'}} onClick={()=>getQuote(sellBal)}>Balance: {sellBal}</span>
                            </div>
                        </div>

                        {/* Swap direction button */}
                        <div style={{display:'flex',justifyContent:'center',margin:'-14px 0',position:'relative',zIndex:1}}>
                            <button onClick={swapDirection} style={{width:40,height:40,borderRadius:12,background:'#2a2a3e',border:'3px solid #0a0a14',color:'#fff',fontSize:18,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>↓</button>
                        </div>

                        {/* Buy card */}
                        <div style={{background:'#1a1a1a',borderRadius:20,padding:'16px 20px',marginTop:4,marginBottom:16,border:'1px solid rgba(255,255,255,0.06)'}}>
                            <div style={{color:'#888',fontSize:13,marginBottom:12}}>Buy</div>
                            <div style={{display:'flex',alignItems:'center',gap:12}}>
                                <span style={{flex:1,color:'#fff',fontSize:36,fontWeight:'300'}}>{buyAmount||'0'}</span>
                                <TokenPicker tokens={tokens} selected={buyToken} onSelect={setBuyToken} />
                            </div>
                            <div style={{fontSize:13,color:'#888',marginTop:8}}>{buyUsdValue||'$0'}</div>
                        </div>

                        {/* Swap info */}
                        { hasAmount && buyAmount && (
                            <div style={{background:'rgba(255,255,255,0.04)',borderRadius:12,padding:'12px 16px',marginBottom:16,fontSize:13}}>
                                <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                                    <span style={{color:'#888'}}>Rate</span>
                                    <span style={{color:'#fff'}}>1 {sellToken} ≈ {(parseFloat(buyAmount)/parseFloat(sellAmount)).toFixed(4)} {buyToken}</span>
                                </div>
                                <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                                    <span style={{color:'#888'}}>Slippage</span>
                                    <span style={{color:'#fff'}}>{slippage}%</span>
                                </div>
                                <div style={{display:'flex',justifyContent:'space-between'}}>
                                    <span style={{color:'#888'}}>Network</span>
                                    <span style={{color:'#fff'}}>{isSolana?'Solana':(selectedNetwork?.charAt(0).toUpperCase()+selectedNetwork?.slice(1)||'Ethereum')}</span>
                                </div>
                            </div>
                        )}

                        {/* Action button */}
                        { !walletAddress ? (
                            <button style={{width:'100%',padding:'16px',background:'transparent',border:'2px solid rgba(255,255,255,0.1)',borderRadius:20,color:'#fff',fontSize:16,fontWeight:'700',cursor:'pointer'}}>
                                Connect Wallet
                            </button>
                        ) : !hasAmount ? (
                            <button style={{width:'100%',padding:'16px',background:'rgba(255,255,255,0.08)',border:'none',borderRadius:20,color:'#888',fontSize:16,fontWeight:'700',cursor:'not-allowed'}}>
                                Enter amount
                            </button>
                        ) : (
                            <button onClick={handleSwap} disabled={isSwapping} style={{width:'100%',padding:'16px',background:'linear-gradient(135deg,#7C63FF,#FC72FF)',border:'none',borderRadius:20,color:'#fff',fontSize:16,fontWeight:'700',cursor:isSwapping?'not-allowed':'pointer'}}>
                                { isSwapping ? 'Swapping...' : (isSolana?'Swap on Jupiter':'Swap') }
                            </button>
                        )}

                        { walletAddress && (
                            <div style={{textAlign:'center',marginTop:12,fontSize:12,color:'#555'}}>
                                🔑 {walletAddress.slice(0,6)}...{walletAddress.slice(-4)} connected
                            </div>
                        )}
                    </>
                )}


            </div>
        </div>
    );
}
